import { View, Text, TextRule, Settings } from '@manychat/mudra'
import ReactPIXI from '@manychat/react-pixi'

import ManagedText from 'common/builder/components/chart/FlowChart/Camera/ManagedText'
import { Colors } from 'common/builder/components/chart/FlowChart/nodes/shared/styles'
import { DeprecatedFields } from 'common/fields/helpers'
import {
  getSelectedSMSBodyLinkShortener,
  isSMSBodyLinkShortenerDisabled,
} from 'common/settings/selectors/settingsSelectors'
import { BODY_LINK_SHORTENER_REGEX, URL_RULE_REGEX_WITHOUT_CAPTURING } from 'constants/RegExs'
import { formatToShortenedLink } from 'utils/misc'

import app from './app'

Settings.VIEW_RESOLUTION = isWebGL2Supported() ? window.devicePixelRatio : 1

ReactPIXI.registerElement('view', (root, props) => {
  const $el = new View(props.sharedResourceId)
  $el.on('load', () => {
    // Render stage after images load
    app.render()

    if (typeof $el.onLoad === 'function') {
      $el.onLoad()
    }
  })
  return $el
})

ReactPIXI.registerElement('richtext', (root, props) => {
  return new ManagedText()
})

class VariableRule extends TextRule {
  static getRule() {
    return { type: 'variable', match: /{{[^\n{}]{1,}}}/ }
  }

  getText() {
    if (!this.variable) {
      const [nameWithBrackets] = this.text.split('|')
      this.variable = nameWithBrackets.replace('{{', '').replace('}}', '')
    }
    const id = this.variable
    const field = this.parent.fields?.find((field) => field.id === id)
    return field?.caption ?? this.variable
  }

  getWidth() {
    if (this.width !== null) {
      return this.width
    }
    this.width = Text.measureText(this.getText(), this.font).width + this.lineHeight / 2
    return this.width
  }

  draw(ctx, fontMetrics) {
    const fillStyle = ctx.fillStyle
    ctx.fillStyle = '#0084ff'
    const center = this.lineHeight / 2
    const padding = 1
    const boxWidth = this.width - padding * 2
    const boxHeight = this.fontSize + padding * 2
    const boxY = center - boxHeight / 2
    drawRoundedRect(ctx, this.x + padding, this.y + boxY, boxWidth, boxHeight, 6)
    ctx.fill()
    ctx.fillStyle = '#ffffff'
    ctx.fillText(this.getText(), this.x + 8, this.y + center + fontMetrics.baselineOffset)
    ctx.fillStyle = fillStyle
  }
}

class SecondaryTextRule extends TextRule {
  static getRule() {
    return { type: 'secondary_text', match: /<secondary>[\S ]+<\/secondary>/ }
  }

  getText() {
    return this.text.slice(11, -12)
  }

  getWidth() {
    if (this.width !== null) {
      return this.width
    }
    this.width = Text.measureText(this.getText(), this.font).width
    return this.width
  }

  draw(ctx, fontMetrics) {
    const fillStyle = ctx.fillStyle
    ctx.fillStyle = Colors.SECONDARY
    ctx.fillText(this.getText(), this.x, this.y + this.lineHeight / 2 + fontMetrics.baselineOffset)
    ctx.fillStyle = fillStyle
  }
}

class BoldTextRule extends TextRule {
  static getRule() {
    return { type: 'bold_text', match: /<b>[\S ]+<\/b>/ }
  }

  getText() {
    return this.text.slice(3, -4)
  }

  getWidth() {
    if (this.width !== null) {
      return this.width
    }
    this.width = Text.measureText(this.getText(), this.font).width
    return this.width
  }

  draw(ctx, fontMetrics) {
    const font = ctx.font
    if (!font.includes('bold')) {
      ctx.font = `bold ${font}`
    }
    ctx.fillText(this.getText(), this.x, this.y + this.lineHeight / 2 + fontMetrics.baselineOffset)
    ctx.font = font
  }
}

class URLRule extends TextRule {
  static getRule() {
    return { type: 'url_link', match: URL_RULE_REGEX_WITHOUT_CAPTURING }
  }

  getWidth() {
    if (this.width !== null) {
      return this.width
    }
    this.width = Text.measureText(this.text, this.font).width
    return this.width
  }

  draw(ctx, fontMetrics) {
    const fillStyle = ctx.fillStyle
    ctx.fillStyle = Colors.PRIMARY
    ctx.fillText(this.text, this.x, this.y + this.lineHeight / 2 + fontMetrics.baselineOffset)
    ctx.fillStyle = fillStyle
  }
}

class BodyURLShortenerRule extends TextRule {
  static getRule() {
    return { type: 'body_url_shortener', match: BODY_LINK_SHORTENER_REGEX }
  }

  getText() {
    const [, urlWithBrackets] = this.text.split('|')
    if (typeof urlWithBrackets === 'string') {
      this.shortenerURL = urlWithBrackets.replace('>>', '')
    }
    const state = window.app.store.getState()
    const selected = getSelectedSMSBodyLinkShortener(state)
    return isSMSBodyLinkShortenerDisabled(state)
      ? this.shortenerURL
      : formatToShortenedLink(selected)
  }

  getWidth() {
    if (this.width !== null) {
      return this.width
    }
    this.width = Text.measureText(this.getText(), this.font).width + this.lineHeight / 2
    return this.width
  }

  draw(ctx, fontMetrics) {
    const fillStyle = ctx.fillStyle
    ctx.fillStyle = Colors.PRIMARY
    ctx.fillText(this.getText(), this.x, this.y + this.lineHeight / 2 + fontMetrics.baselineOffset)
    ctx.fillStyle = fillStyle
  }
}

Text.registerTextRule(URLRule)
Text.registerTextRule(SecondaryTextRule)
Text.registerTextRule(BoldTextRule)
Text.registerTextRule(VariableRule)
Text.registerTextRule(BodyURLShortenerRule)

// Deprecation matched. Used for displaying deprecated variables/fields/etc
DeprecatedFields.forEach((deprecation) => {
  class DeprecationRule extends TextRule {
    static getRule() {
      return { type: 'deprecated', match: deprecation.pattern }
    }

    getText() {
      return deprecation.getCaption(this.text)
    }

    getWidth() {
      if (this.width !== null) {
        return this.width
      }
      this.width = Text.measureText(this.getText(), this.font).width + this.lineHeight / 2
      return this.width
    }

    draw(ctx, fontMetrics) {
      const fillStyle = ctx.fillStyle
      const font = ctx.font

      ctx.strokeStyle = '#0084ff'
      const center = this.lineHeight / 2
      const padding = 1
      const boxWidth = this.width - padding * 2
      const boxHeight = this.fontSize + padding * 2
      const boxY = center - boxHeight / 2
      drawRoundedRect(ctx, this.x + padding, this.y + boxY, boxWidth, boxHeight, 6)
      ctx.stroke()
      ctx.fillStyle = '#0084ff'
      ctx.font = this.font
      ctx.fillText(this.getText(), this.x + 8, this.y + center + fontMetrics.baselineOffset)

      ctx.fillStyle = fillStyle
      ctx.font = font
    }
  }

  Text.registerTextRule(DeprecationRule)
})

/**
 * Draws a rounded rectangle using the current state of the canvas.
 * If you omit the last three params, it will draw a rectangle
 * outline with a 5 pixel border radius
 * @param {CanvasRenderingContext2D} ctx
 * @param {Number} x The top left x coordinate
 * @param {Number} y The top left y coordinate
 * @param {Number} width The width of the rectangle
 * @param {Number} height The height of the rectangle
 * @param {Number} radius The corner radius. Defaults to 5;
 */
function drawRoundedRect(ctx, x, y, width, height, radius = 0) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

function isWebGL2Supported() {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('webgl2')
  return ctx != null
}
